import { NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { Component, inject, Injector, OnDestroy, OnInit, output, signal } from '@angular/core';
import {
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  StockDocInventoryDto,
  StockDocInventoryService,
} from '@digiwall/goliath-documents/proxy';
import StockDocLinesEditorComponent, {
  StockDocLinesImports,
  StockDocLinesProviders,
} from '../../../stock-doc-lines-editor.component';
import { LocalizationService } from '@abp/ng.core';
import {
  ImportComponent,
  ImportStatusDto,
  ImportStatusHubService,
  ObserverQueue,
  OdysseyOffcanvas,
} from '@digiwall/core';
import { StorageLocationService } from '@digiwall/goliath-core/proxy';
import { StockDocLineEditorHelperService } from '../../../../../components';
import { ColDef, EditableCallback, ValueFormatterParams } from '@ag-grid-community/core';
import { ImportStatus } from '@digiwall/core/proxy';
import { ToasterService } from '@abp/ng.theme.shared';
import { first, tap } from 'rxjs';

@Component({
  selector: 'document-stock-doc-inventory-lines-grid',
  templateUrl: './stock-doc-inventory-lines-editor.component.html',
  imports: StockDocLinesImports,
  providers: StockDocLinesProviders,
})
export default class StockDocInventoryLinesEditorComponent
  extends StockDocLinesEditorComponent<StockDocInventoryDto, StockDocInventoryService>
  implements OnInit, OnDestroy
{
  protected readonly offcanvasService = inject(OdysseyOffcanvas);

  importLineEnded = output<void>();
  actionBtns = [
    {
      label: this.localizationService.instant('Doc::StockDoc:ImportLines'),
      action: () => {
        const canvaRef = this.offcanvasService.open(ImportComponent, null, {
          position: 'end',
          panelClass: 'w-100 w-sm-75 w-3xl-50 ',
        });

        this.canvasRef = canvaRef;
        this.canvasRef.closed
          .pipe(
            first(),
            tap(() => {
              this.canvasRef = null;
            }),
          )
          .subscribe();
        let componentInstance = canvaRef.componentInstance as ImportComponent;
        componentInstance.catalogName = 'StockDocInventoryLine';
        componentInstance.extraProperties = {
          DocId: this.document.id,
        };
      },
      disabled: signal(false),
    },
  ];

  private canvasRef: NgbOffcanvasRef | null = null;

  protected override get articleLineRole() {
    return DocLineRole.StockInventoryArticleLine;
  }

  constructor(
    localizationService: LocalizationService,
    docService: StockDocInventoryService,
    storageLocationService: StorageLocationService,
    injector: Injector,
    editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    updateQueue: ObserverQueue,
    private toastService: ToasterService,
    private importStatusHubService: ImportStatusHubService,
  ) {
    super(
      localizationService,
      docService,
      storageLocationService,
      injector,
      editorHelper,
      updateQueue,
    );
  }

  ngOnInit(): void {
    this.importStatusHubService.startConnection().subscribe(() => {
      this.importStatusHubService.receiveImportStatus().subscribe((message: ImportStatusDto) => {
        if (
          message.catalogName === 'StockDocInventoryLine' &&
          message.extraProperties.DocId == this.document.id
        ) {
          if (message.statusId == ImportStatus.Completed) {
            this.canvasRef?.close();
          } else if (message.statusId == ImportStatus.Failed) {
            this.toastService.error(this.localizationService.instant('::Import:ImportFailed'));
          }
          this.importLineEnded.emit();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.importStatusHubService.endConnection();
  }

  protected override initColDefs(): ColDef<any, any>[] {
    const colDefs = super.initColDefs();
    colDefs.find(x => x.field == 'unitPrice')!.editable = false;

    // inventory documents can change serial quantity (validation 0/1 only is in the backend)
    var colIndex = colDefs.findIndex(x => x.field == this.colDef_Quantity.field);
    colDefs.splice(colIndex, 1, Object.assign({}, this.colDef_Quantity));

    colIndex = colDefs.findIndex(x => x.field == this.colDef_Quantity.field);
    colDefs.splice(
      colIndex + 1,
      0,
      Object.assign({}, this.colDef_Quantity, {
        field: 'quantityInPurchaseUnit',
        headerName: this.localizationService.instant('Doc::QuantityInPurchaseUnit'),
        editable: false,
      } as Partial<ColDef>),
    );

    colIndex = colDefs.findIndex(x => x.field == 'articleSalesUnitCode');
    colDefs.splice(
      colIndex + 1,
      0,
      Object.assign({}, colDefs[colIndex], {
        field: 'articlePurchaseUnitCode',
        headerName: this.localizationService.instant('Product::Article:ProductPurchaseUnit'),
        valueFormatter: (params: ValueFormatterParams<any, any>) =>
          params?.data?.articleUsesUnitConversion ? params.value : '',
      } as Partial<ColDef>),
    );

    colIndex = colDefs.findIndex(x => x.field == this.colDef_UnitPrice.field);
    colDefs.splice(
      colIndex + 1,
      0,
      Object.assign({}, this.colDef_UnitPrice, {
        field: 'articlePurchaseUnitPrice',
        headerName: this.localizationService.instant('Doc::PurchaseUnitPrice'),
        editable: false,
      } as Partial<ColDef>),
    );
    return colDefs;
  }
}
